// BULK SELECTION ON REFERENCES AND RULES (owner, 2026-09-14: "Rules and
// References would have buttons for Mark Active (green) and Mark Inactive
// (red)").
//
// ONE SHAPE, THE ONE ASSIGNMENTS USES: a checkbox on each row, an unlabelled
// Select all in the table's own top-left header cell, and the actions in a
// strip above the table carrying the count — "Mark 3 Active". Nothing about the
// row's own behaviour changes: clicking it still opens its editor, because the
// checkbox stops the click before the row sees it.
//
// THE STRIP IS NOT THERE UNTIL SOMETHING IS TICKED (owner, 2026-09-15: "I liked
// it better when our tables were relatively flush underneath the sub-tabs ...
// put the Select All checkbox in the top-left corner within the table,
// unlabeled, and drop 'Select all' while hiding the action buttons until rows
// are actually selected"). It was a permanent 40px row carrying a labelled
// Select all and two greyed-out buttons, on ten lists, on every visit — so the
// first row of every table sat a control's height below the sub-tabs whether or
// not anyone ever used it. Select all moved into the header cell above the
// column it controls, where the label was only restating the column anyway.
//
// THE WRITE IS N SEQUENTIAL PATCHES, not a batch. There is no bulk route and
// this does not invent one: each list already has a per-row update that carries
// its own `row_version`, its own authority check and its own audit, and a batch
// endpoint would need all three again server-side for a control that acts on
// what is on one screen. Sequential also means a refusal stops at the row that
// caused it and the caller can say which — a partial batch could not.
//
// SELECTION IS WHAT IS ON SCREEN. `useBulkSelection` is handed the visible,
// filtered, sorted rows, and Select all ticks exactly those — the same rule
// `AssignmentsScreen` states for its own. A tick that reached past what the
// reader can see is how a bulk action surprises someone.
//
// ON SCREEN MEANS THE CURRENT PAGE, on the one list that pages (QA wave B1,
// B1-02). Vendors was handed its whole filtered result — 161 rows behind a
// pager reading `1–50 of 161` — so Select all offered `Mark 161 Inactive` over
// fifty visible rows and 111 nobody could see. Hand a paging list the PAGE.
// Paging through to reach the rest is the cost, and it is the right cost: the
// other direction is a status change on records that were never on screen.
//
// ONE KEY FUNCTION PER LIST, and it comes from here (QA wave B1, B1-01). The
// selection used to be keyed by the caller's `keyOf` while the row's own
// checkbox was keyed by a separate `rowKey` prop; on References the two were
// the business id and the row uuid, so `toggle` wrote a key `has` could never
// read back. Every row checkbox on Vendors, Companies and all three Codes
// sub-views was inert, and Select all ticked a set no row rendered. The write
// was fine — only the reading of it was broken, which is why the strip still
// counted. `useBulkMark` returns the `keyOf` it used, and the table takes that
// same function; there is no second key to disagree with it.
//
// TWO LISTS DO NOT SAY "ACTIVE" AND "INACTIVE", and the reason is the same both
// times — their listing is not an independent flag:
//   * Vehicles marks IN SERVICE / OUT OF SERVICE (owner, 2026-09-14: "then offer
//     In Service and Out of Service"). Its `is_active` is a database CHECK over
//     the fleet's own Status (`vehicles_active_follows_status`, D-65), so Active
//     and Inactive are not values the list can be asked for — but the fleet's
//     own Status is, and setting it is exactly what a fleet manager does. `Sold`
//     is deliberately not one of the two: it is a fact about a disposal, not a
//     listing decision, and stays a per-row edit.
//   * Rules -> Companies > Uploaders is ONE ENTRY OVER UP TO THREE STORED ROWS
//     — the intake allow-list row, the routing row and the format default
//     (D-379), each with its own lifecycle (`lib/senders.ts`) — so its write
//     patches whichever of them the entry actually has, to the same value. What
//     it cannot do is CREATE a routing row
//     that never existed: "Mark Active" over an intake-only entry leaves it
//     intake-only, which is the same thing its own editor does and is why its
//     Status is `entryIsActive` (does this entry still do anything) rather than
//     a flag of its own.
// Both are stated here rather than left to the code, because a list whose two
// buttons say something different from the other eight reads as an oversight.
import { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@fluentui/react-components'
import { closeSentence, countOf } from './format'
import { refusalSentence } from '../api/errors'

export interface BulkSelection {
  /** Keys currently ticked, filtered to what is still on screen. */
  selected: ReadonlySet<string>
  /** Is this key ticked? */
  has: (key: string) => boolean
  /** Tick or untick one row. */
  toggle: (key: string) => void
  /** Tick every visible row, or clear when they all already are. */
  toggleAll: () => void
  /** Every visible row is ticked. */
  allSelected: boolean
  /** How many are ticked. */
  count: number
  /** Drop the selection — called after a write, so the bar does not offer the
      same action again over rows that have already taken it. */
  clear: () => void
}

/**
 * A selection together with the ONE function that names its rows.
 *
 * A table renders a row's checkbox from `keyOf(row)` rather than from a key of
 * its own, so the key the checkbox writes is by construction the key the
 * selection reads. See this file's header for the defect that shape exists to
 * make impossible.
 */
export interface RowBulk<T> {
  selection: BulkSelection
  keyOf: (row: T) => string
}

/**
 * The ticked keys that are still on screen.
 *
 * Exported so the rule can be tested without a DOM, because breaking it is
 * silent: a selection keyed by one thing and rows keyed by another intersect
 * to nothing, every checkbox renders unticked, and no error is raised
 * anywhere (QA wave B1, B1-01).
 */
export function visibleSelection(
  raw: Iterable<string>,
  visibleKeys: readonly string[],
): Set<string> {
  const visible = new Set(visibleKeys)
  return new Set([...raw].filter((k) => visible.has(k)))
}

/** The sentence a completed bulk mark reports. `asked` is what the button
    said; `done` is what actually changed, which is fewer whenever a ticked row
    was already in the requested state.

    THE ROW EDITOR'S OWN WORDS (QA wave B, 2026-09-25, B-14; D-170). The strip
    said *"1 rule marked inactive."* where the editor, for the same act, says
    *"The rule is now Inactive."* One vocabulary now: one row is *"The rule is
    now Inactive."*, several are *"3 rules are now Inactive."*. `word` is the
    state as the list's own buttons name it — *Active*, *Inactive*, *In
    Service*, *Out of Service*. */
export function bulkMarkMessage(done: number, asked: number, noun: string, word: string): string {
  const led = done === 1 ? `The ${noun} is now ${word}.` : `${countOf(done, noun)} are now ${word}.`
  if (done === asked) return led
  const rest = asked - done
  return `${led} The other ${rest} already ${rest === 1 ? 'was' : 'were'}.`
}

/** The sentence when a bulk mark stops at a refusal part-way: how many of
    the rows it set out to write did land, then why it stopped. The verb agrees
    with the count — *"1 of 20 is now Inactive."*, *"12 of 20 are now
    Inactive."* (second review of QA wave B, 2026-09-25) — and `word` is the
    state's name as `bulkMarkMessage` takes it. */
export function bulkStoppedMessage(done: number, total: number, word: string, reason: string): string {
  return `${done} of ${total} ${done === 1 ? 'is' : 'are'} now ${word}. Then it stopped: ${reason}`
}

/** A bulk mark refused before anything was written: two full sentences, the
    server's reason closed with its period (QA 2026-10-04 A-06 — it read
    "Nothing was changed: An active guidance row …" with no period). */
export function nothingChangedBecause(reason: string): string {
  return `Nothing was changed. ${closeSentence(reason.trim())}`
}

/** The sentence when every ticked row was already in the requested state.
    "0 marked" reads as a failure; this does not. `word` as `bulkMarkMessage`. */
export function nothingToChangeMessage(noun: string, word: string): string {
  return `Nothing to change — every selected ${noun} is already ${word}.`
}

/**
 * Selection over the rows a list is currently SHOWING.
 *
 * `visibleKeys` changes as the filter and the search do, and the selection is
 * intersected with it on every read: ticking a row and then filtering it away
 * must not leave it in a bulk write the reader can no longer see.
 */
function useBulkSelection(visibleKeys: readonly string[]): BulkSelection {
  const [raw, setRaw] = useState<ReadonlySet<string>>(() => new Set())
  const selected = useMemo(() => visibleSelection(raw, visibleKeys), [raw, visibleKeys])
  const toggle = useCallback((key: string) => {
    setRaw((prev) => {
      const next = new Set(prev)
      if (!next.delete(key)) next.add(key)
      return next
    })
  }, [])
  const allSelected = visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k))
  const toggleAll = useCallback(() => {
    setRaw(allSelected ? new Set() : new Set(visibleKeys))
  }, [allSelected, visibleKeys])
  const clear = useCallback(() => setRaw(new Set()), [])
  return {
    selected,
    has: (key) => selected.has(key),
    toggle,
    toggleAll,
    allSelected,
    count: selected.size,
    clear,
  }
}


/**
 * Selection plus the write, for a list whose rows each have their own update.
 *
 * The write is SEQUENTIAL and stops at the first refusal, reporting it with the
 * count that did land: "12 of 20 are now Inactive. Then it stopped: …"
 * (`bulkStoppedMessage`). A row
 * already in the requested state is skipped rather than patched, so pressing
 * Mark Active over a mixed selection costs one request per row that actually
 * changes and the audit records only real changes.
 */
export function useBulkMark<T>({
  rows,
  keyOf,
  isActive,
  write,
  noun,
  labels,
  onDone,
  onError,
}: {
  /** The rows currently on screen, filtered and sorted — and on a list that
      pages, the CURRENT PAGE (see this file's header). */
  rows: readonly T[]
  keyOf: (row: T) => string
  isActive: (row: T) => boolean
  write: (row: T, active: boolean) => Promise<unknown>
  /** Singular, for the toast: "vendor", "rule". Pluralized by `pluralize`. */
  noun: string
  /** What the two states are CALLED, where a list does not say active and
      inactive — Vehicles says In Service / Out of Service, and said "active"
      and "inactive" in its toast while its buttons, its column and its filter
      all said otherwise (QA wave B1, B1-07). The same pair goes to `BulkBar`,
      which is what makes the button and the sentence agree. */
  labels?: { active: string; inactive: string }
  /** Fired once every write has landed, with a sentence for the toast. */
  onDone: (message: string) => void
  onError: (message: string) => void
}): {
  selection: BulkSelection
  busy: boolean
  mark: (active: boolean) => void
  /** The very function the selection is keyed by — hand it to the table so the
      row's checkbox cannot be keyed by anything else. */
  keyOf: (row: T) => string
} {
  const keys = useMemo(() => rows.map(keyOf), [rows, keyOf])
  const selection = useBulkSelection(keys)
  const [busy, setBusy] = useState(false)

  const mark = useCallback(
    (active: boolean) => {
      // As the buttons say it, capitals kept: the state's name (B-14).
      const word = active ? labels?.active ?? 'Active' : labels?.inactive ?? 'Inactive'
      const targets = rows.filter((r) => selection.has(keyOf(r)) && isActive(r) !== active)
      if (targets.length === 0) {
        onDone(nothingToChangeMessage(noun, word))
        selection.clear()
        return
      }
      const asked = selection.count
      void (async () => {
        setBusy(true)
        let done = 0
        try {
          for (const row of targets) {
            await write(row, active)
            done += 1
          }
          // SAY SO WHEN THE COUNT IS NOT THE ONE ON THE BUTTON. A row already
          // in the requested state is skipped rather than patched, so pressing
          // `Mark 4 Active` over three already-active rows writes one — and
          // reporting a bare "1 marketing category …" beside a
          // button that said 4 reads as a partial failure (QA wave B1, B1-04).
          onDone(bulkMarkMessage(done, asked, noun, word))
          selection.clear()
        } catch (e) {
          // The server's own sentence where it wrote one (B-16), as the row
          // editor shows it.
          const reason = refusalSentence(e) ?? (e instanceof Error ? e.message : String(e))
          onError(
            done === 0
              ? nothingChangedBecause(reason)
              : bulkStoppedMessage(done, targets.length, word, reason),
          )
        } finally {
          setBusy(false)
        }
      })()
    },
    [rows, selection, keyOf, isActive, write, noun, labels, onDone, onError],
  )

  return { selection, busy, mark, keyOf }
}

/** The class for the leading checkbox column, so every table sizes it the same
    way. A hook rather than a constant because `makeStyles` has to run inside a
    component — the same shape `tableDensity.ts` uses. */
export function useBulkCellClass(): string {
  return useCellStyles().cell
}

const useCellStyles = makeStyles({ cell: { width: '44px' } })
